import React, { lazy, Suspense, useState, useEffect, useMemo } from 'react';
import { LoadingSpinner } from 'components/common';
import { useSelector, useDispatch } from 'react-redux';
import { FaRegListAlt, FaSync } from 'react-icons/fa';
import { MdDashboard } from 'react-icons/md';
import OrderIntakeHeader from '../../../components/app/crm/shipment/orders/OrderIntakeHeader';
import OrderIntakeDashboard from '../../../components/app/crm/shipment/orders/OrderIntakeDashboard';
import { LuSigma } from "react-icons/lu";
import MonthYearPicker from '../../../components/common/MonthYearPicker';
import { setNestedParams, getOrderIntakes, exportData } from "redux/crm/shipment/orderIntakeSlice";
import { endOfMonth, format } from "date-fns";
import { toast } from 'react-toastify';
import { MdDownload } from 'react-icons/md';
import Tabs from '../../../components/common/Tabs';
const LazyOrderIntake = lazy(() => import("../../../components/app/crm/shipment/orders/OrderIntake"));
const LazyOrderIntakeSumYTD = lazy(() => import("../../../components/app/crm/shipment/orders/OrderIntakeSumYTD"));

const ShipmentOrderIntakePage = () => {
  const [selected, setSelected] = useState([]);
  const [monthYearValue, setMonthYearValue] = useState(() => format(new Date(), 'MMM-yyyy'));
  const [isExporting, setIsExporting] = useState(false);
  const [activeTab, setActiveTab] = useState('shipment-order-overview');
  const { loading } = useSelector((state) => state.crm_shipment_order_intake);
  const [params, setParams] = useState({});
  const [pageReady, setPageReady] = useState(false);

  const dispatch = useDispatch();
  const key = "allData";

  useEffect(() => {
    const [month, year] = monthYearValue.split('-');
    const monthIndex = new Date(`${month} 1, ${year}`).getMonth();
    const tempDate = new Date(parseInt(year), monthIndex, 1);
    
    const yearMonth = format(tempDate, 'yyyy-MM');
    const startDate = `${yearMonth}-01`;
    const endDate = format(endOfMonth(tempDate), 'yyyy-MM-dd');

    const newData = {
      period_date_gteq: startDate,
      period_date_lteq: endDate
    }
    setParams(newData);

    dispatch(setNestedParams({key, params: newData}));
    // Load summary + rows at page level so the Overview tab (now the default) has
    // data even before the Detail table mounts.
    dispatch(getOrderIntakes(newData));
  }, [monthYearValue, dispatch]);

  const handleRefresh = async () => {
    try {
      await dispatch(getOrderIntakes(params)).unwrap();
    }
    catch(error) {
      toast.error("Failed to refresh order intakes.");
    }
  }

  const handleMonthYearChange = (value) => {
    setMonthYearValue(value);
  }

  const handleExport = async () => {
    if (isExporting) return; // Prevent multiple clicks
    
    try {
      setIsExporting(true);
      await dispatch(exportData(params)).unwrap();
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => { setPageReady(true); }, []);

  const handleTabChange = (tabIndex) => {
    const tabId = tabsData[tabIndex]?.id;
    if (tabId) {
      setActiveTab(tabId);
    }
  };

  const tabsData = useMemo(() => [
    {
      id: 'shipment-order-overview',
      label: 'Overview',
      icon: <MdDashboard size={18} />
    },
    {
      id: 'shipment-order-all',
      label: 'Detail',
      icon: <FaRegListAlt size={16} />
    },
    {
      id: 'shipment-order-sum',
      label: 'Sum YTD',
      icon: <LuSigma size={16} />
    }
  ], []);

  const activeTabIndex = useMemo(() => {
    return tabsData.findIndex(tab => tab.id === activeTab);
  }, [activeTab, tabsData]);

  if (!pageReady) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-min-h-screen tw-bg-gray-50 tw-gap-4">
        <LoadingSpinner size="lg" />
        <p className="tw-text-sm tw-text-gray-500 tw-animate-pulse">Loading Order Intake…</p>
      </div>
    );
  }

  return (
    <div className="tw-w-full tw-h-full tw-flex tw-flex-col tw-gap-3 tw-p-4">
      <OrderIntakeHeader selected={selected} period={monthYearValue} />

      <div className="tw-flex tw-flex-col lg:tw-flex-row tw-justify-between tw-items-start lg:tw-items-center tw-gap-3 tw-bg-white tw-border tw-border-gray-200 tw-rounded tw-p-4 tw-shadow-sm">
        <div className="tw-flex tw-items-center tw-gap-3">
          <label className="tw-text-sm tw-font-medium tw-text-gray-700 tw-whitespace-nowrap">
            Period date:
          </label>
          <MonthYearPicker
            value={monthYearValue}
            onChange={handleMonthYearChange}
            placeholder="Select month"
            clearable={false}
            inputClassName="tw-h-10 tw-text-sm"
            minWidth={200}
          />
        </div>

        <div className="tw-flex tw-items-center tw-gap-2">
          <button
            title="Refresh"
            onClick={handleRefresh}
            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-slate-300 tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50 tw-text-sm tw-cursor-pointer"
          >
            <FaSync size={13} />
            <span className="tw-hidden sm:tw-inline">Refresh</span>
          </button>
          <button
            title="Export"
            onClick={handleExport}
            disabled={isExporting}
            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-green-300 tw-bg-green-50 tw-text-green-700 hover:tw-bg-green-100 tw-text-sm tw-font-medium tw-cursor-pointer disabled:tw-opacity-50"
          >
            <MdDownload size={14} />
            <span className="tw-hidden sm:tw-inline">{isExporting ? 'Exporting...' : 'Export'}</span>
          </button>
        </div>
      </div>

      {loading &&
        <div className="tw-text-center tw-my-2">
          <LoadingSpinner />
        </div>
      }

      <div className="tw-rounded tw-border tw-border-gray-200 tw-bg-white tw-shadow-sm tw-overflow-hidden">
        <Tabs
          tabs={tabsData}
          activeTab={activeTabIndex}
          onChange={handleTabChange}
          variant="underline"
          navClassName="tw-px-3 tw-pt-1"
        />
        <div className="tw-bg-gray-50 tw-p-4">
          <Suspense fallback={
            <div className="tw-text-center tw-my-2">
              <LoadingSpinner />
            </div>
          }>
            {activeTab === 'shipment-order-overview' && (
              <OrderIntakeDashboard />
            )}
            {activeTab === 'shipment-order-all' && (
              <LazyOrderIntake monthYear={monthYearValue} setSelected={setSelected} />
            )}
            {activeTab === 'shipment-order-sum' && (
              <LazyOrderIntakeSumYTD monthYear={monthYearValue} />
            )}
          </Suspense>
        </div>
      </div>
    </div>
  )
}

export default ShipmentOrderIntakePage;